import { useState, useEffect } from "react";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { useNavigate, useSearchParams } from "react-router-dom";
import Navbar from "@/components/Navbar";
import { Card } from "@/components/ui/card";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { SprintStageManager } from "@/components/sprint/SprintStageManager";
import { SprintDashboard } from "@/components/sprint/SprintDashboard";
import { CreateSprintForm } from "@/components/sprint/CreateSprintForm";
import { Skeleton } from "@/components/ui/skeleton";
import { useAuth } from "@/contexts/AuthContext";
import { ArrowLeft, ArrowRight, Calendar, ChevronDown, Plus, Users } from "lucide-react";
import { Button } from "@/components/ui/button";
import { formatDistanceToNow } from "date-fns";
import sprintProcessDiagram from "@/assets/ideas-sprint-diagram-new.png";

const PromptArchitectureSprint = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [activeSprint, setActiveSprint] = useState<any>(null);
  const [recentSprints, setRecentSprints] = useState<any[]>([]);
  const [selectedSprint, setSelectedSprint] = useState<any>(null);
  const [view, setView] = useState<'dashboard' | 'create' | 'sprint'>('dashboard');
  const [isExplanationOpen, setIsExplanationOpen] = useState(false);

  useEffect(() => {
    if (user) {
      loadSprints();
    } else {
      setLoading(false);
    }
  }, [user]);

  // Handle sprint query parameter (from invitation links)
  useEffect(() => {
    const sprintId = searchParams.get('sprint');
    if (sprintId && user && !loading) {
      loadSpecificSprint(sprintId);
    }
  }, [searchParams, user, loading]);

  const loadSprints = async () => {
    try {
      // Run all queries in parallel for faster loading
      const [activeResult, recentResult, participantResult] = await Promise.all([
        // Query 1: Active sprint where user is creator
        supabase
          .from('prompt_architecture_sprints')
          .select(`
            *,
            sprint_contributions(*)
          `)
          .eq('creator_id', user?.id)
          .eq('status', 'in_progress')
          .order('created_at', { ascending: false })
          .limit(1)
          .maybeSingle(),
        
        // Query 2: Recent sprints where user is creator
        supabase
          .from('prompt_architecture_sprints')
          .select(`
            *,
            sprint_contributions(*)
          `)
          .eq('creator_id', user?.id)
          .in('status', ['completed', 'paused'])
          .order('created_at', { ascending: false })
          .limit(6),
        
        // Query 3: Sprints where user is a participant (but not creator)
        supabase
          .from('sprint_participants')
          .select(`
            sprint_id,
            prompt_architecture_sprints(
              *,
              sprint_contributions(*)
            )
          `)
          .eq('user_id', user?.id)
          .neq('prompt_architecture_sprints.creator_id', user?.id)
      ]);

      // Handle errors from parallel queries
      if (activeResult.error) throw activeResult.error;
      if (recentResult.error) throw recentResult.error;
      if (participantResult.error) throw participantResult.error;

      // Extract sprint data from participant sprints
      const participantSprintsData = (participantResult.data || [])
        .map(p => p.prompt_architecture_sprints)
        .filter(Boolean);

      // Check if there's an active participant sprint
      const activeParticipantSprint = participantSprintsData.find(s => s.status === 'in_progress');
      
      // Combine recent sprints
      const recentParticipantSprints = participantSprintsData.filter(s => 
        s.status === 'completed' || s.status === 'paused'
      );

      // Update state once with all data
      setActiveSprint(activeResult.data || activeParticipantSprint || null);
      setRecentSprints([...(recentResult.data || []), ...recentParticipantSprints].slice(0, 6));
    } catch (error: any) {
      toast({
        title: "Failed to load sprints",
        description: error.message,
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const loadSpecificSprint = async (sprintId: string) => {
    try {
      const { data: sprint, error } = await supabase
        .from('prompt_architecture_sprints')
        .select(`
          *,
          sprint_contributions(*)
        `)
        .eq('id', sprintId)
        .single();

      if (error) throw error;

      setSelectedSprint(sprint);
      setView('sprint');
    } catch (error: any) {
      toast({
        title: "Failed to load sprint",
        description: error.message,
        variant: "destructive"
      });
    }
  };

  const handleCreateSprint = async (objective: string, invitees: { userIds: string[], emails: string[] }) => {
    setCreating(true);
    try {
      // 1. Create the sprint
      const { data, error } = await supabase
        .from('prompt_architecture_sprints')
        .insert({
          creator_id: user?.id,
          objective,
          status: 'in_progress'
        })
        .select(`
          *,
          sprint_contributions(*)
        `)
        .single();

      if (error) throw error;

      // 2. Add creator as first participant
      const { error: participantError } = await supabase
        .from('sprint_participants')
        .insert({
          sprint_id: data.id,
          user_id: user!.id,
          role: 'creator'
        });

      if (participantError) console.error('Error adding creator as participant:', participantError);

      // 3. Send in-platform invitations to network members
      if (invitees.userIds.length > 0) {
        // Get inviter's display name for notification
        const { data: inviterProfile } = await supabase
          .from('profiles')
          .select('display_name')
          .eq('id', user!.id)
          .single();
        
        const inviterName = inviterProfile?.display_name || 'A team member';

        // Get user profiles for display names
        const { data: profiles } = await supabase
          .from('profiles')
          .select('id, display_name, email')
          .in('id', invitees.userIds);

        const invitationPromises = (profiles || []).map(async (profile) => {
          // Create sprint invitation record and get its ID
          const { data: invitation, error: invError } = await supabase
            .from('sprint_invitations')
            .insert({
              sprint_id: data.id,
              inviter_id: user!.id,
              invitee_user_id: profile.id,
              invitee_email: profile.email,
              invitee_name: profile.display_name,
              status: 'pending'
            })
            .select('id')
            .single();

          if (invError) {
            console.error('Error creating invitation:', invError);
            return;
          }

          // Create notification for the invitee with ALL required metadata
          await supabase.from('notifications').insert({
            user_id: profile.id,
            type: 'sprint_invitation',
            title: 'Sprint Invitation',
            message: `You've been invited to join a prompt architecture sprint: "${objective.substring(0, 50)}${objective.length > 50 ? '...' : ''}"`,
            action_url: `/prompt-architecture-sprint?sprint=${data.id}`,
            metadata: {
              requires_response: true,
              sprint_id: data.id,
              sprint_objective: objective,
              inviter_id: user!.id,
              inviter_name: inviterName,
              invitation_id: invitation.id
            }
          });

          // Create sent notification for the sender
          await supabase.from('sent_notifications').insert({
            sender_id: user!.id,
            recipient_id: profile.id,
            type: 'sprint_invitation',
            title: 'Sprint Invitation Sent',
            message: `Invited ${profile.display_name} to sprint: "${objective.substring(0, 50)}${objective.length > 50 ? '...' : ''}"`,
            action_url: `/prompt-architecture-sprint?sprint=${data.id}`,
            metadata: { sprint_id: data.id, invitee_id: profile.id, invitation_id: invitation.id }
          });
        });

        await Promise.all(invitationPromises);
      }

      // 4. Send email invitations to non-platform users
      if (invitees.emails.length > 0) {
        try {
          await supabase.functions.invoke('send-sprint-invitation', {
            body: {
              sprint_id: data.id,
              sprint_objective: objective,
              inviter_id: user!.id,
              invitee_emails: invitees.emails
            }
          });
        } catch (emailError) {
          console.error('Error sending email invitations:', emailError);
          // Don't fail the sprint creation if emails fail
        }
      }

      setActiveSprint(data);
      setSelectedSprint(data);
      setView('sprint');
      
      const inviteCount = invitees.userIds.length + invitees.emails.length;
      toast({ 
        title: "Sprint created successfully!",
        description: inviteCount > 0 ? `${inviteCount} invitation${inviteCount > 1 ? 's' : ''} sent.` : undefined
      });
    } catch (error: any) {
      toast({
        title: "Failed to create sprint",
        description: error.message,
        variant: "destructive"
      });
    } finally {
      setCreating(false);
    }
  };

  const handleStageComplete = async () => {
    await loadSprints();
  };

  const handleBackToDashboard = () => {
    setView('dashboard');
    setSelectedSprint(null);
    loadSprints();
  };

  if (!user) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar />
        <div className="container mx-auto px-4 py-12">
          <div className="max-w-6xl mx-auto">
            <div className="text-center space-y-4 mb-8">
              <h1 className="text-4xl font-bold">Prompt Architecture Sprint</h1>
              <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
                Collaborative prompt development through a structured 4-stage process
              </p>
            </div>
            <Card className="p-12 text-center">
              <h2 className="text-2xl font-semibold mb-4">Sign in to create sprints</h2>
              <p className="text-muted-foreground mb-6">
                You need to be signed in to create and manage prompt architecture sprints
              </p>
              <Button size="lg" onClick={() => navigate('/auth')}>
                Sign In
              </Button>
            </Card>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      
      <div className="container mx-auto px-4 py-12">
        <div className="max-w-6xl mx-auto space-y-8">
          {/* Header - Always Visible */}
          <div className="text-center space-y-4">
            <h1 className="text-4xl font-bold">IDEAS Prompt Architecture Sprint</h1>
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
              Collaborate with your network to advance prompts that build better knowledge bases for AI
            </p>
          </div>

          {/* Loading State - Matches final layout to prevent shift */}
          {loading && (
            <div className="space-y-8">
              {/* Launch sprint card skeleton */}
              <Skeleton className="h-48 w-full" />
              
              {/* Active sprint card skeleton */}
              <Skeleton className="h-48 w-full" />
              
              {/* Diagram skeleton */}
              <div className="flex justify-center py-8">
                <Skeleton className="h-96 w-full max-w-3xl rounded-lg" />
              </div>
              
              {/* Collapsible skeleton */}
              <Skeleton className="h-16 w-full" />
              
              {/* Recent sprints skeleton */}
              <div className="grid md:grid-cols-3 gap-4">
                <Skeleton className="h-32" />
                <Skeleton className="h-32" />
                <Skeleton className="h-32" />
              </div>
            </div>
          )}

          {/* Main Content - Only render when not loading */}
          {!loading && (
            <>
              {view === 'dashboard' && (
                <>
                  {/* Launch a Sprint Card */}
                  <Card className="p-8 text-center hover:shadow-lg transition-shadow cursor-pointer border-2 border-dashed" onClick={() => setView('create')}>
                    <div className="max-w-md mx-auto space-y-4">
                      <div className="w-16 h-16 bg-green-600 rounded-full flex items-center justify-center mx-auto">
                        <Plus className="w-8 h-8 text-white" />
                      </div>
                      <h3 className="text-xl font-semibold">Launch a Sprint</h3>
                      <p className="text-muted-foreground">
                        Collaborate with your team to architect powerful prompts through our 4-stage process
                      </p>
                    </div>
                  </Card>

                  {/* Active Sprint Card */}
                  {activeSprint && (
                    <Card className="p-6 bg-gradient-to-br from-primary/10 to-primary/5 border-primary/20 min-h-[200px]">
                      <div className="flex items-start justify-between mb-4">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            <span className="px-2 py-1 bg-primary/20 text-primary text-xs font-medium rounded">
                              In Progress
                            </span>
                            <span className="text-sm text-muted-foreground">
                              {formatDistanceToNow(new Date(activeSprint.created_at), { addSuffix: true })}
                            </span>
                          </div>
                          <h3 className="text-xl font-semibold mb-2">{activeSprint.objective}</h3>
                          <div className="flex items-center gap-4 text-sm text-muted-foreground">
                            <div className="flex items-center gap-1">
                              <Calendar className="w-4 h-4" />
                              <span>Stage: {!activeSprint.inform_stage_completed ? "Inform" : !activeSprint.decide_stage_completed ? "Decide" : !activeSprint.execute_stage_completed ? "Execute" : !activeSprint.assess_stage_completed ? "Assess & Sustain" : "Completed"}</span>
                            </div>
                            <div className="flex items-center gap-1">
                              <Users className="w-4 h-4" />
                              <span>{activeSprint.sprint_contributions?.length || 0} contributions</span>
                            </div>
                          </div>
                        </div>
                        <Button onClick={() => {
                          setSelectedSprint(activeSprint);
                          setView('sprint');
                        }} size="lg">
                          Continue Sprint
                          <ArrowRight className="ml-2 h-4 w-4" />
                        </Button>
                      </div>
                      
                      {/* Progress Bar */}
                      <div className="mt-4">
                        <div className="flex justify-between text-xs text-muted-foreground mb-2">
                          <span>Progress</span>
                          <span>{Math.round((
                            [activeSprint.inform_stage_completed, activeSprint.decide_stage_completed, activeSprint.execute_stage_completed, activeSprint.assess_stage_completed].filter(Boolean).length / 4
                          ) * 100)}%</span>
                        </div>
                        <div className="h-2 bg-muted rounded-full overflow-hidden">
                          <div 
                            className="h-full bg-primary transition-all duration-300"
                            style={{ width: `${(
                              [activeSprint.inform_stage_completed, activeSprint.decide_stage_completed, activeSprint.execute_stage_completed, activeSprint.assess_stage_completed].filter(Boolean).length / 4
                            ) * 100}%` }}
                          />
                        </div>
                      </div>
                    </Card>
                  )}

                  {/* Sprint Process Diagram */}
                  <div className="flex justify-center py-8">
                    <img 
                      src={sprintProcessDiagram} 
                      alt="Prompt Architecture Sprint Process - A continuous cycle of Inform, Decide, Execute, and Assess & Sustain stages with Giblet's Learning Model at the center"
                      className="max-w-3xl w-full h-auto rounded-lg"
                    />
                  </div>

                  {/* What is an IDEAS Sprint - Collapsible */}
                  <Collapsible open={isExplanationOpen} onOpenChange={setIsExplanationOpen}>
                    <Card className="p-6 bg-gradient-to-br from-accent/10 to-accent/5 border-accent/20">
                      <CollapsibleTrigger asChild>
                        <button className="w-full flex items-center justify-between">
                          <h2 className="text-2xl font-bold">What is an IDEAS Sprint?</h2>
                          <ChevronDown className={`h-6 w-6 transition-transform duration-200 ${isExplanationOpen ? 'rotate-180' : ''}`} />
                        </button>
                      </CollapsibleTrigger>
                      
                      <CollapsibleContent className="pt-6">
                        <p className="text-lg mb-8 text-center max-w-5xl mx-auto">
                          iSPAI&apos;s team-based collaboration process where teams create validated knowledge together working with AI under smart contract governance.
                        </p>
                        
                        <div className="space-y-8">
                          {/* How it works */}
                          <div className="border border-black/20 rounded-lg p-4">
                            <h3 className="text-lg font-semibold mb-4">How it works:</h3>
                            <div className="grid md:grid-cols-4 gap-4">
                              <div>
                                <div className="text-3xl mb-2">💡</div>
                                <h4 className="font-medium mb-1">1. Inform</h4>
                                <p className="text-sm text-muted-foreground">
                                  Upload files, link knowledge bases, or add context to inform your sprint
                                </p>
                              </div>
                              <div>
                                <div className="text-3xl mb-2">🗳️</div>
                                <h4 className="font-medium mb-1">2. Decide</h4>
                                <p className="text-sm text-muted-foreground">
                                  AI analyzes your content and generates strategic decisions to vote on
                                </p>
                              </div>
                              <div>
                                <div className="text-3xl mb-2">⚡</div>
                                <h4 className="font-medium mb-1">3. Execute</h4>
                                <p className="text-sm text-muted-foreground">
                                  Generate and refine prompts with real-time AI outputs
                                </p>
                              </div>
                              <div>
                                <div className="text-3xl mb-2">⭐</div>
                                <h4 className="font-medium mb-1">4. Assess & Sustain</h4>
                                <p className="text-sm text-muted-foreground">
                                  Rate contributions and directly impact team GibRatings
                                </p>
                              </div>
                            </div>
                          </div>

                          {/* Participants Benefits */}
                          <div>
                            <h3 className="font-semibold text-lg mb-3">Participants are automatically:</h3>
                            <div className="grid md:grid-cols-3 gap-4">
                              <div className="flex items-start gap-3 p-4 bg-background/50 rounded-lg">
                                <div className="text-2xl">📊</div>
                                <div>
                                  <h4 className="font-medium mb-1">Credited and scored</h4>
                                  <p className="text-sm text-muted-foreground">
                                    Role + Ownership % + GibRating
                                  </p>
                                </div>
                              </div>
                              <div className="flex items-start gap-3 p-4 bg-background/50 rounded-lg">
                                <div className="text-2xl">💰</div>
                                <div>
                                  <h4 className="font-medium mb-1">Paid through digital wallets</h4>
                                  <p className="text-sm text-muted-foreground">
                                    Connected through mySPAI profiles
                                  </p>
                                </div>
                              </div>
                              <div className="flex items-start gap-3 p-4 bg-background/50 rounded-lg">
                                <div className="text-2xl">🔒</div>
                                <div>
                                  <h4 className="font-medium mb-1">Protected by smart contracts</h4>
                                  <p className="text-sm text-muted-foreground">
                                    Smart contract terms and on-chain attribution
                                  </p>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </CollapsibleContent>
                    </Card>
                  </Collapsible>

                  {/* Recent Sprints via SprintDashboard */}
                  <SprintDashboard
                    activeSprint={null}
                    recentSprints={recentSprints}
                    onCreateNew={() => setView('create')}
                    onResumeSprint={(sprint) => {
                      setSelectedSprint(sprint);
                      setView('sprint');
                    }}
                    onViewSprint={(sprint) => {
                      setSelectedSprint(sprint);
                      setView('sprint');
                    }}
                    showCreateCard={false}
                  />
                </>
              )}

              {view === 'create' && (
                <CreateSprintForm
                  userId={user?.id}
                  onSubmit={handleCreateSprint}
                  onCancel={() => setView('dashboard')}
                  creating={creating}
                />
              )}

              {view === 'sprint' && selectedSprint && (
                <div className="space-y-4">
                  <Button
                    variant="ghost"
                    onClick={handleBackToDashboard}
                    className="mb-4"
                  >
                    <ArrowLeft className="mr-2 h-4 w-4" />
                    Back to Dashboard
                  </Button>
                  <SprintStageManager
                    sprint={selectedSprint}
                    userId={user?.id || ''}
                    onStageComplete={handleStageComplete}
                  />
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default PromptArchitectureSprint;
